import type {
  ModifyProfilePayload,
  ProfileAccountBranch,
  ProfileAccountUser,
  ProfileAddress,
  ProfileIdentity,
  ProfileImageClickOutcome,
  ProfileRestrictedField,
} from './profileAccount.types';

const ADDRESS_FIELDS: Array<keyof ProfileAddress> = ['street', 'country', 'state', 'city', 'country_code', 'postal_code'];

/** Address fields the "outstanding info" banner and `hasUserAddress` look at (country_code is derived). */
const ADDRESS_USER_FIELDS: Array<keyof ProfileAddress> = ['city', 'country', 'street', 'state', 'postal_code'];

function orEmpty(value: string | null | undefined): string {
  return value ? value : '';
}

function hasKeys(value: object | null | undefined): value is object {
  return !!value && Object.keys(value).length > 0;
}

/** Port of `ProfileController.showTaxIdOnProfile`. */
export function showTaxIdOnProfile(branch: ProfileAccountBranch | null | undefined): boolean {
  return !!branch?.configuration?.fiscal?.member_tax_id_options?.enabled;
}

function isFilled(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.length > 0;
}

/** Port of `ProfileController.hasUserAddress`: any user-entered address field is filled. */
export function hasUserAddress(user: ProfileAccountUser | null | undefined): boolean {
  const address = user?.address;
  if (!hasKeys(address)) {
    return false;
  }
  return ADDRESS_USER_FIELDS.some((field) => isFilled(address[field]));
}

function isBlank(value: string | null | undefined): boolean {
  return value === '';
}

function hasBlankContact(profile: ProfileIdentity): boolean {
  return isBlank(profile.phone) || isBlank(profile.emergency_contact);
}

function hasBlankTaxId(profile: ProfileIdentity, branch: ProfileAccountBranch | null | undefined): boolean {
  const fiscal = profile.metadata?.fiscal;
  return showTaxIdOnProfile(branch) && !!fiscal && isBlank(fiscal.tax_id);
}

function hasBlankAddressField(profile: ProfileIdentity): boolean {
  const address = profile.address;
  return hasKeys(address) && ADDRESS_USER_FIELDS.some((field) => isBlank(address[field]));
}

/**
 * Port of `ProfileController.outstandingProfileInfo`. The controller only
 * flags fields the API returned as '' (never `undefined`), which is kept here.
 */
export function outstandingProfileInfo(
  profile: ProfileIdentity | null | undefined,
  branch: ProfileAccountBranch | null | undefined
): boolean {
  if (!profile) {
    return false;
  }
  return hasBlankContact(profile) || hasBlankTaxId(profile, branch) || hasBlankAddressField(profile);
}

export type ProfileImageGateContext = {
  hasUser: boolean;
  hasBranch: boolean;
  /** `is-restrict-personal-information-change-enabled`. */
  updateProfileRestricted: boolean;
  /** Result of the HEAD check on `profile.image_url`. */
  hasUploadedProfileImage: boolean;
};

/** Port of `ProfileController.userCanChangeImage`: a restricted branch only allows the first upload. */
export function userCanChangeImage(context: ProfileImageGateContext): boolean {
  if (!context.hasUser || !context.hasBranch) {
    return false;
  }
  return !context.updateProfileRestricted || !context.hasUploadedProfileImage;
}

/** Port of `vm.onProfileImageInputClick`: what tapping the image input should do. */
export function resolveProfileImageClick(
  context: Pick<ProfileImageGateContext, 'updateProfileRestricted' | 'hasUploadedProfileImage'>
): ProfileImageClickOutcome {
  if (!context.updateProfileRestricted) {
    return 'open-file-picker';
  }
  return context.hasUploadedProfileImage ? 'show-restricted-dialog' : 'show-first-time-dialog';
}

/**
 * Whether tapping an edit-form field opens the "profile restricted" dialog.
 * Phone is the exception: an empty phone can always be filled in.
 */
export function shouldShowRestrictedDialogForField(
  field: ProfileRestrictedField,
  profile: ProfileIdentity | null | undefined,
  updateProfileRestricted: boolean
): boolean {
  if (!updateProfileRestricted) {
    return false;
  }
  if (field === 'phone') {
    return !!profile?.phone;
  }
  return true;
}

/**
 * `ng-disabled` of an edit-form field once the restricted dialog has been
 * shown (`vm.isInputDisabled`); an empty phone stays editable.
 */
export function isRestrictedFieldDisabled(
  field: ProfileRestrictedField,
  profile: ProfileIdentity | null | undefined,
  isInputDisabled: boolean
): boolean {
  if (field === 'phone') {
    return profile?.phone ? isInputDisabled : false;
  }
  return isInputDisabled;
}

/**
 * Port of the body built in `ProfileController.modifyProfile`. `consent` is
 * the output of `UserService.getUserConsentObjectUpdated` and passes through.
 */
export function buildModifyProfilePayload(
  profile: ProfileIdentity,
  consent: unknown,
  branch: ProfileAccountBranch | null | undefined
): ModifyProfilePayload {
  const payload: ModifyProfilePayload = {
    first_name: orEmpty(profile.first_name),
    last_name: orEmpty(profile.last_name),
    email: orEmpty(profile.email),
    phone: orEmpty(profile.phone),
    consent,
    emergency_contact: orEmpty(profile.emergency_contact),
  };

  if (showTaxIdOnProfile(branch)) {
    payload.metadata = { fiscal: { tax_id: orEmpty(profile.metadata?.fiscal?.tax_id) } };
  }

  if (profile.address) {
    const address = profile.address;
    payload.address = ADDRESS_FIELDS.reduce(
      (result, field) => ({ ...result, [field]: orEmpty(address[field]) }),
      {} as NonNullable<ModifyProfilePayload['address']>
    );
  }

  return payload;
}
